import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Header, MobileNav } from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Bell, CheckCircle, ChatCircle, Star, Dog, Clock, BellRinging, BellSlash } from '@phosphor-icons/react';
import { formatRelativeTime } from '../lib/constants';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const notificationIcons = {
  new_request: Dog,
  request_accepted: CheckCircle,
  request_rejected: Clock,
  request_completed: CheckCircle,
  new_message: ChatCircle,
  new_review: Star,
};

export default function NotificationsPage() {
  const { user } = useAuth();
  const { notifications, markAsRead, markAllAsRead, unreadCount } = useNotifications();
  const { isSupported, isSubscribed, permission, loading: pushLoading, subscribe, unsubscribe } = usePushNotifications();

  const handlePushToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      const result = await subscribe();
      if (result.reason === 'denied') {
        toast.error('Notificações push bloqueadas pelo browser. Ative nas definições.');
      }
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification.notification_id);
    }
  };

  const getNotificationLink = (notification) => {
    const data = notification.data || {};
    const isProvider = user?.role === 'prestador';
    
    if (data.request_id) {
      return isProvider ? `/provider/requests` : `/client/requests`;
    }
    return '#';
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Header />
      
      <main className="container mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold font-outfit">Notificações</h1>
            <div className="flex gap-2">
              {isSupported && (
                <Button
                  variant={isSubscribed ? 'default' : 'outline'}
                  size="sm"
                  onClick={handlePushToggle}
                  disabled={pushLoading}
                  className="rounded-full"
                  data-testid="push-toggle-btn"
                >
                  {isSubscribed ? (
                    <><BellRinging className="w-4 h-4 mr-1" /> Push Ativo</>
                  ) : (
                    <><BellSlash className="w-4 h-4 mr-1" /> Ativar Push</>
                  )}
                </Button>
              )}
              {unreadCount > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={markAllAsRead}
                  className="rounded-full"
                  data-testid="mark-all-read"
                >
                  Marcar Todas como Lidas
                </Button>
              )}
            </div>
          </div>

          {notifications.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Bell weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Sem notificações</h3>
                <p className="text-muted-foreground text-sm">
                  As suas notificações aparecerão aqui
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {notifications.map((notification) => {
                const Icon = notificationIcons[notification.type] || Bell;
                
                return (
                  <Link 
                    key={notification.notification_id}
                    to={getNotificationLink(notification)}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <Card className={`provider-card ${!notification.read ? 'bg-primary/5 border-primary/20' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            !notification.read ? 'bg-primary/10' : 'bg-muted'
                          }`}>
                            <Icon className={`w-5 h-5 ${!notification.read ? 'text-primary' : 'text-muted-foreground'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h3 className={`font-medium ${!notification.read ? 'text-primary' : ''}`}>
                                {notification.title}
                              </h3>
                              <span className="text-xs text-muted-foreground">
                                {formatRelativeTime(notification.created_at)}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {notification.message}
                            </p>
                          </div>
                          {!notification.read && (
                            <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </motion.div>
      </main>

      <MobileNav />
    </div>
  );
}
